import { createFileRoute, Link } from '@tanstack/react-router'
import { useState } from 'react'
import { EmptyState } from '@/components/empty-state'
import { Button } from '@/components/ui/button'
import {
  useOutreachConfig,
  useOutreachQueue,
  useOutreachStats,
  useRedraftAll,
} from '@/lib/outreach'
import { SectionHeader, TableSkeleton, useSiteScope } from '../../-section'
import { QueueTable } from '../-draft'

export const Route = createFileRoute('/_app/$org/$host/outreach/_list/')({
  component: DraftsPage,
})

/**
 * The one gate: every mail is read here before anything can send it. The
 * machine found the page, read it, decided it was worth writing to and wrote
 * the mail; what is left is whether the person whose name is on it agrees.
 */
function DraftsPage() {
  const { org, host, site } = useSiteScope()
  const { data, isPending } = useOutreachQueue(site, 'draft')
  const { data: stats } = useOutreachStats(site)
  const { data: config } = useOutreachConfig(site)

  if (isPending && !data) {
    return (
      <>
        <SectionHeader title="Drafts" />
        <TableSkeleton rows={3} />
      </>
    )
  }

  const items = data?.items ?? []

  if (items.length === 0) {
    const off = config && !config.config?.enabled
    const reading = stats?.stats.prospects.researching ?? 0

    return (
      <>
        <SectionHeader title="Drafts" />
        <div className="rounded-lg border border-border bg-card">
          <EmptyState
            title={
              off
                ? 'Outreach is off'
                : reading > 0
                  ? `Reading ${reading} pages`
                  : 'Nothing to approve'
            }
            description={
              off
                ? `Nothing is being found or written for ${host}.`
                : `No mail is waiting on you for ${host}.`
            }
            action={
              off ? (
                <Button asChild size="sm">
                  <Link to="/$org/$host/outreach/settings" params={{ org, host }}>
                    Set outreach up
                  </Link>
                </Button>
              ) : undefined
            }
          />
        </div>
      </>
    )
  }

  return (
    <>
      <SectionHeader
        title="Drafts"
        actions={<RedraftAll siteId={site?.id} count={items.length} />}
      />
      <QueueTable items={items} org={org} host={host} />
    </>
  )
}

/** Asks before it acts: every draft is written again, and edits made to one
 * by hand go with it. */
function RedraftAll({ siteId, count }: { siteId: string | undefined; count: number }) {
  const redraft = useRedraftAll(siteId)
  const [confirming, setConfirming] = useState(false)

  if (!confirming) {
    return (
      <Button size="sm" variant="outline" onClick={() => setConfirming(true)}>
        Redraft all
      </Button>
    )
  }

  return (
    <>
      <Button size="sm" variant="ghost" onClick={() => setConfirming(false)}>
        Cancel
      </Button>
      <Button
        size="sm"
        disabled={redraft.isPending}
        onClick={() => redraft.mutate(undefined, { onSettled: () => setConfirming(false) })}
      >
        Redraft {count} {count === 1 ? 'draft' : 'drafts'}
      </Button>
    </>
  )
}
